import React, { useState, useEffect, useMemo } from 'react';
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  FormControl,
  Menu,
  Box,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Collapse,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useAppState } from 'store/Provider';
import { selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';

type mockOptions = Record<string, string[]>;

interface CollapsibleSelectFilterProps {
  name: string;
  apiKeyname: string;
  label: string;
  disabled?: boolean;
  mockOptions: mockOptions;
  value: string[] | 'ALL';
  onChange: (selectedValues: string[] | 'ALL') => void;
  clearPerticularFilters: (apiKeyname: string) => void;
}

const CollapsibleCountryFilter: React.FC<CollapsibleSelectFilterProps> = ({
  name,
  apiKeyname,
  label,
  mockOptions,
  value,
  onChange,
  clearPerticularFilters,
}) => {
  const { dispatch } = useAppState();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [checkedOptions, setCheckedOptions] = useState<Record<string, boolean>>({});
  const [checkedCountries, setCheckedCountries] = useState<Record<string, boolean>>({});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  const allCities = useMemo(() => Object.values(mockOptions || {}).flat(), [mockOptions]);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    const initialCities: Record<string, boolean> = {};
    const initialCountries: Record<string, boolean> = {};

    if (value === 'ALL') {
      allCities.forEach((city) => {
        initialCities[city] = true;
      });
      Object.keys(mockOptions || {}).forEach((country) => {
        initialCountries[country] = true;
      });
      setCheckedOptions(initialCities);
      setCheckedCountries(initialCountries);
      setIsSelectAllChecked(true);
    } else {
      const selectedArray = Array.isArray(value) ? value : [];

      // Check which are countries and which are cities
      Object.entries(mockOptions || {}).forEach(([country, cities]) => {
        let cityList: string[] = [];
        if (Array.isArray(cities)) {
          cityList = cities;
        } else if (cities && typeof cities === 'object') {
          cityList = Object.values(cities).flat() as string[];
        }

        if (selectedArray.includes(country)) {
          // Country is selected
          initialCountries[country] = true;
          cityList.forEach((city) => {
            initialCities[city] = true;
          });
        } else {
          // Check individual cities
          initialCountries[country] = false;
          cityList.forEach((city) => {
            initialCities[city] = selectedArray.includes(city);
          });
        }
      });

      setCheckedOptions(initialCities);
      setCheckedCountries(initialCountries);
      setIsSelectAllChecked(false);
    }
  }, [allCities, value, mockOptions]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setSearch('');
    setAnchorEl(null);
  };

  const toggleGroup = (country: string) => {
    setExpandedGroups((prev) => ({ ...prev, [country]: !prev[country] }));
  };

  const handleCityChange = (city: string, checked: boolean) => {
    setCheckedOptions((prev) => ({ ...prev, [city]: checked }));
  };

  const handleCountryToggle = (country: string, cities: string[], checked: boolean) => {
    const updated = { ...checkedOptions };
    cities.forEach((city) => {
      updated[city] = checked;
    });
    setCheckedOptions(updated);
    setCheckedCountries((prev) => ({ ...prev, [country]: checked }));
  };

  const handleApply = () => {
    const selected: string[] = [];

    // First, add all checked countries
    Object.entries(checkedCountries).forEach(([country, isChecked]) => {
      if (isChecked) {
        selected.push(country);
      }
    });

    // Then, add individual cities that are checked but their country is not checked
    Object.entries(mockOptions || {}).forEach(([country, cities]) => {
      if (!checkedCountries[country]) {
        let cityList: string[] = [];
        if (Array.isArray(cities)) {
          cityList = cities;
        } else if (cities && typeof cities === 'object') {
          cityList = Object.values(cities).flat() as string[];
        }

        cityList.forEach((city) => {
          if (checkedOptions[city]) {
            selected.push(city);
          }
        });
      }
    });

    if (selected.length === allCities.length && allCities.length > 0) {
      dispatch(selectFilterAction({ [apiKeyname]: ['ALL'] }));
      onChange('ALL');
    } else if (selected.length === 0) {
      dispatch(selectFilterAction({ [apiKeyname]: [] }));
      onChange([]);
    } else {
      dispatch(selectFilterAction({ [apiKeyname]: selected }));
      onChange(selected);
    }
    handleClose();
  };

  const handleClear = () => {
    setCheckedOptions({});
    setCheckedCountries({});
    onChange([]);
    clearPerticularFilters(apiKeyname);
    dispatch(selectFilterRemoveAction({ [name]: 'ALL' }));
    handleClose();
  };

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // Display value logic to match the design
  let displayValue = '';
  if (value === 'ALL') {
    displayValue = 'ALL';
  } else if (Array.isArray(value) && value.length > 0) {
    displayValue = value.join(', ');
  }

  return (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth>
        {/* Label outside the TextField */}
        <Typography
          component="label"
          htmlFor={`collapsible-country-filter-${name}`}
          sx={{
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'primary.main',
            mb: 0.5,
            display: 'block',
          }}
        >
          {label}
        </Typography>

        <TextField
          id={`collapsible-country-filter-${name}`}
          value={displayValue}
          onClick={handleOpen}
          readOnly
          fullWidth
          size="small"
          placeholder="Select..."
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {displayValue && displayValue !== 'ALL' && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <ArrowDropDownIcon sx={{ cursor: 'pointer', color: 'action.active' }} />
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: anchorEl ? anchorEl.clientWidth : 320,
            maxHeight: 450,
            mt: 0.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Search Box - Fixed at top */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
              },
            }}
          />
        </Box>

        {/* Scrollable Content Area */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1, maxHeight: 300 }}>
          {/* ALL Checkbox */}
          <MenuItem sx={{ py: 1, px: 2, borderBottom: '1px solid #e0e0e0' }} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              size="small"
              checked={isSelectAllChecked}
              onChange={(e) => {
                const { checked } = e.target;
                const updatedCities: Record<string, boolean> = {};
                const updatedCountries: Record<string, boolean> = {};
                allCities.forEach((city) => {
                  updatedCities[city] = checked;
                });
                Object.keys(mockOptions || {}).forEach((country) => {
                  updatedCountries[country] = checked;
                });
                setCheckedOptions(updatedCities);
                setCheckedCountries(updatedCountries);
                setIsSelectAllChecked(checked);
              }}
              sx={{ p: 0.5, mr: 1 }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              ALL
            </Typography>
          </MenuItem>

          {/* Country/City List */}
          <Box>
            {Object.entries(mockOptions || {}).map(([country, cities]) => {
              let cityList: string[] = [];

              if (Array.isArray(cities)) {
                cityList = cities;
              } else if (cities && typeof cities === 'object') {
                cityList = Object.values(cities).flat() as string[];
              }

              const filteredCities = cityList.filter(
                (city) =>
                  String(city).toLowerCase().includes(search.toLowerCase()) ||
                  country.toLowerCase().includes(search.toLowerCase())
              );

              if (filteredCities.length === 0) return null;

              const isAllChecked = filteredCities.every((city) => checkedOptions[city]);
              const isSomeChecked = filteredCities.some((city) => checkedOptions[city]);
              const isExpanded = expandedGroups[country] || search.length > 0;

              return (
                <Box key={country}>
                  {/* Country Header */}
                  <Box
                    sx={{
                      py: 1.2,
                      px: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      minHeight: 50,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                    onClick={() => toggleGroup(country)}
                  >
                    <IconButton
                      size="small"
                      sx={{ p: 0, width: 24, height: 24 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(country);
                      }}
                    >
                      {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                    <Checkbox
                      size="small"
                      checked={isAllChecked}
                      indeterminate={isSomeChecked && !isAllChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleCountryToggle(country, cityList, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.5 }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                      {country}
                    </Typography>
                  </Box>

                  {/* City List */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 6, pr: 1.5, py: 0.5, backgroundColor: '#fafafa' }}>
                      {filteredCities.map((city) => (
                        <Box
                          key={city}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            py: 1.2,
                            minHeight: 50,
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={checkedOptions[city] || false}
                            onChange={(e) => handleCityChange(city, e.target.checked)}
                            sx={{ p: 0.5, mr: 1 }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                            {city}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Action Buttons - Fixed at bottom */}
        <Box
          onMouseDown={stop}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 1.5,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
            flexShrink: 0,
          }}
        >
          <Button size="small" onClick={handleClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleApply} sx={{ textTransform: 'none' }}>
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default CollapsibleCountryFilter;
